import _ from "lodash";
import React, { Fragment, isValidElement, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classes from "./AudioRecorderSelector.scss";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import UIModal from "UIComponents/UIModal";
import AudioRecorder from "UIComponents/AudioRecorder";
import AudioRecorderV2 from "UIComponents/AudioRecorderV2";
import AudioRecorderVisualiser from "UIComponents/AudioRecorderVisualiser";
import SpeechToTextVisualiser from "UIComponents/SpeechToTextVisualiser";
import AudioRecorderV4 from "UIComponents/AudioRecorderV4";
import I18nHOC from "UIComponents/I18nHOC";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import Portal from "UIComponents/Portal";
import AudioRecorderV3 from "UIComponents/AudioRecorderV3";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { colors } from "Constants";
import { CancelIcon } from "SvgComponents";
import { getAttachmentSendObj, onEnterOrSpaceKeyDown, mergeRefs } from "Utils";
import {
  uploadFile,
  removeProgressElement,
  createAttachment,
  deleteAttachment,
  downloadFromUrl,
  setAudioRecorderActive,
} from "modules/Services";
import classNames from "classnames";
import { MicrophoneOutlined, DeleteOutlined } from "@toddle-edu/ds-icons";
import { Dialog, Tooltip } from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { AudioRecorderV5 } from "../AudioRecorderV5/AudioRecorderV5";

const styles = {
  control: {
    borderTop: `1px solid ${colors.strokeOne}`,
  },
  buttonStyles: {
    flex: "1",
  },
};

const DIALOGS_INFO = {
  title: "delete_with_label",
  message: "delete_msg_with_label",
  confirmButtonText: "delete_agree",
  cancelButtonText: "agree_cancel",
  label: "voice_instruction",
};

const LOADER_SIZE = {
  "x-small": "24px",
  medium: "32px",
  small: "28px",
};

const LoaderSVG = props => {
  const { size = "medium", loaderStyles } = props;
  const loaderRef = React.useRef(null);

  useEffect(() => {
    if (loaderRef.current) {
      loaderRef.current.focus();
    }
  }, []);

  const loaderWrapperStyles = {
    width: LOADER_SIZE[size],
    height: LOADER_SIZE[size],
    ...loaderStyles,
  };

  return (
    <button
      className={classes.loaderContainer}
      style={loaderWrapperStyles}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
      ref={loaderRef}
      aria-label="Initialising recorder"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 50 50"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke="#090909ff"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="90,150"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 25 25"
            to="360 25 25"
            dur="1s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    </button>
  );
};

const CustomMediaPlaceholder = ({
  attachments,
  deleteAudio,
  mode,
  isUploading,
  t,
  onDeleteClick,
  downloadFromUrl,
  isLastAttachment,
  dialogsInfo,
  showAudioDownloadButton = true,
  customMediaWrapperStyle,
  showWaveform,
  testId,
  isDownloadEnabled,
  showCompactAudioWaveformPlayer,
  isAppearanceLikeMediaCard,
  customDeleteButton,
  mediaPlaceholderContainerStyle,
  playAudioOnSeek,
}) => {
  const [showModal, setShowModal] = useState(false);

  const onDialogConfirmClick = () => {
    deleteAudio();
    setShowModal(false);
  };

  const onCloseDialogueClick = () => {
    setShowModal(false);
  };

  const setDialogueBoxStatus = e => {
    onDeleteClick ? onDeleteClick(e) : setShowModal(true);
  };

  /**
   * ! Not used any where
   */
  const onDownloadClick = e => {
    e.stopPropagation();
    downloadFromUrl({
      attachment: _.first(attachments),
      shouldAskForSave: true,
    });
  };

  const isValidCustomDeleteButton = isValidElement(customDeleteButton);

  const DELETE_ICON_WRAPPER_ELEMENT = isValidCustomDeleteButton
    ? "div"
    : "button";

  const handleDeleteButtonKeyDown = e => {
    if (isValidCustomDeleteButton) return;
    onEnterOrSpaceKeyDown(e, setDialogueBoxStatus);
  };

  return (
    <Fragment>
      <div
        className={classes.customMediaWrapper}
        style={customMediaWrapperStyle}
      >
        <MediaPlaceholder
          attachments={attachments}
          showFileName={false}
          mode={mode}
          isUploading={isUploading}
          isLastAttachment={isLastAttachment}
          showAudioDownloadButton={showAudioDownloadButton}
          showWaveform={showWaveform}
          testId={testId}
          isDownloadEnabled={isDownloadEnabled}
          showCompactAudioWaveformPlayer={showCompactAudioWaveformPlayer}
          isAppearanceLikeMediaCard={isAppearanceLikeMediaCard}
          containerStyle={mediaPlaceholderContainerStyle}
          playAudioOnSeek={playAudioOnSeek}
        />
        {mode === "edit" && !isUploading && (
          <DELETE_ICON_WRAPPER_ELEMENT
            data-test-id={`${testId}-deleteMedia`}
            className={classes.deleteIcon}
            onClick={setDialogueBoxStatus}
            tabIndex={!isValidCustomDeleteButton ? 0 : undefined}
            onKeyDown={handleDeleteButtonKeyDown}
          >
            {isValidCustomDeleteButton ? (
              customDeleteButton
            ) : (
              <DeleteOutlined size="xxx-small" />
            )}
          </DELETE_ICON_WRAPPER_ELEMENT>
        )}
        {/* {mode == "view" && (
          <div className={classes.deleteIcon} onClick={onDownloadClick}>
            <DownloadIcon />
          </div>
        )} */}
      </div>
      {showModal && (
        <Dialog
          variant={"delete-dialog"}
          title={t(dialogsInfo.title, {
            label: t(dialogsInfo.label),
          })}
          message={t(dialogsInfo.message, {
            label: t(dialogsInfo.label),
          })}
          onButtonOneClick={onCloseDialogueClick}
          onButtonTwoClick={onDialogConfirmClick}
          buttonOneText={t(dialogsInfo.cancelButtonText)}
          buttonTwoText={t(dialogsInfo.confirmButtonText)}
          onClose={onCloseDialogueClick}
          testId={testId}
        />
      )}
    </Fragment>
  );
};

class AudioRecorderSelector extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      modalVisibility: false,
      isContainerProvided: false,
      showDiscardExistingRecordingModal: false,
      isInitializing: false,
      isLoading: false,
    };
    this.audioRecorderRef = null;
    this.buttonRef = React.createRef();
  }

  componentDidMount() {
    // Expose handleExternalUpdates to parent component via ref
    if (
      this.props.handleExternalUpdates &&
      typeof this.props.handleExternalUpdates === "object"
    ) {
      this.props.handleExternalUpdates.current = this.handleExternalUpdates;
    }
  }

  setModalVisibility = state => () => this.setState({ modalVisibility: state });

  setIsInitializing = state => {
    this.setState({ isInitializing: state }, () => {
      const { version, setChatbarVisible } = this.props;
      if (_.includes(["inline"], version) && setChatbarVisible) {
        setChatbarVisible(this.state.isInitializing);
      }
    });
  };

  handleChatbarVisibility = isVisible => {
    if (_.includes(["visualiser"], this.props.version)) {
      this.props.setChatbarVisible(isVisible);
    }
  };

  onModalClose = async () => {
    const { onModalHide = _.noop } = this.props;

    // stop recorder using ref
    if (this.audioRecorderRef.refs.wrappedInstance)
      await this.audioRecorderRef.refs.wrappedInstance.cancelRecording();
    this.setModalVisibility(false)();
    this.updateRecordingStatus(false);
    this.handleChatbarVisibility(true);
    this.setState({ isInitializing: false });
    onModalHide();

    // Restore focus to trigger button
    if (this.buttonRef?.current && _.isFunction(this.buttonRef.current.focus)) {
      this.buttonRef.current.focus();
    }
  };

  handleAudioFile = async props => {
    const {
      onCancel,
      onBlobAttachmentRecieved,
      shouldAwaitUploadFile,
    } = this.props;
    if (_.isFunction(onBlobAttachmentRecieved)) {
      onBlobAttachmentRecieved(props);
    }
    onCancel && onCancel();

    if (shouldAwaitUploadFile) {
      await this.uploadFile(props);
    } else {
      this.uploadFile(props);
    }
    this.setModalVisibility(false)();
    this.updateRecordingStatus(false);
    this.handleChatbarVisibility(true);
    this.setState({ isLoading: false });

    // Restore focus to trigger button
    if (this.buttonRef?.current && _.isFunction(this.buttonRef.current.focus)) {
      this.buttonRef.current.focus();
    }
  };

  getCreateAttachmentFieldTypeAndId = () => {
    const { fieldId, parentType, name } = this.props;
    let fieldType = "";
    switch (name) {
      case "voiceInstruction":
        if (parentType == "ASSESSMENT")
          fieldType = "ASSESSMENT_VOICE_INSTRUCTION";
        break;
      case "oralPresentationRecording":
      case "oralPresentation":
      case "recording":
      case "individualOralRecording": {
        fieldType = parentType;
      }
    }

    return { fieldId, fieldType };
  };

  createOrDeleteAttachment = async ({ attachment, isDelete = false }) => {
    const { createAttachment, deleteAttachment, portalType } = this.props;
    const { fieldId, fieldType } = this.getCreateAttachmentFieldTypeAndId();

    if (!fieldType) {
      return attachment;
    }
    if (!isDelete) {
      const attachments = await createAttachment({
        attachment,
        nodeId: fieldId,
        type: fieldType,
        parentType: fieldType,
        portalType,
      });
      return _.get(attachments, "length", 0) > 0 ? attachments[0] : {};
    } else {
      deleteAttachment({
        attachmentId: _.get(attachment, "id", ""),
        nodeId: fieldId,
        type: fieldType,
        portalType,
      });
    }
    return attachment;
  };

  uploadFile = async attachment => {
    this.updateUploadingState(true);

    const { file, title } = attachment;
    if (!_.isNil(_.get(attachment, "metadata.duration")) && file) {
      file.duration = _.get(attachment, "metadata.duration");
    }
    const { parentId, parentType, showProgress, onFileUpload } = this.props;

    try {
      const { promise, uploadId } = this.props.uploadFile({
        file,
        startProgress: 0,
        parentType,
        parentId,
        attachment,
        showProgress,
      });

      if (onFileUpload) onFileUpload({ attachment });

      const item = await promise;

      let sendObj = await getAttachmentSendObj(file, item);
      sendObj = await this.createOrDeleteAttachment({ attachment: sendObj });

      this.props.removeProgressElement({ id: uploadId });
      this.updateUploadingState(false);

      const value = {
        id: uploadId,
        thumbUrl: "",
        title: "",
        __typename: "Attachment",
        ...(sendObj || {}),
      };

      this.updateValue(value);
    } catch {
      this.updateUploadingState(false);
    }
  };

  updateUploadingState(value) {
    if (this.props.uploadStatus) {
      this.props.uploadStatus(value);
    }
  }

  updateRecordingStatus = value => {
    if (this.props.updateRecordingStatus) {
      this.props.updateRecordingStatus(value);
    }
  };

  updateIsLoading = value => {
    this.setState({ isLoading: value });
  };

  deleteAudio = async () => {
    const { value } = this.props;
    await this.createOrDeleteAttachment({ attachment: value, isDelete: true });
    this.updateValue(null);
  };

  handleAudioRecording = () => {
    this.setModalVisibility(true)();
    this.updateRecordingStatus(true);
    this.handleChatbarVisibility(false);
    this.setIsInitializing(true);
  };

  handleExternalUpdates = () => {
    this.setModalVisibility(true)();
    this.updateRecordingStatus(true);
    this.handleChatbarVisibility(false);
    this.setIsInitializing(true);
  };

  handleCancelRecording = () => {
    const { onCancel, version } = this.props;
    if (version === "inline") this.props.setChatbarVisible(true);
    this.updateRecordingStatus(false);
    this.handleChatbarVisibility(true);
    this.setState({ isInitializing: false });
    onCancel && onCancel();

    // Set focus back to the button after cancellation
    if (this.buttonRef.current && _.isFunction(this.buttonRef.current.focus)) {
      this.buttonRef.current.focus();
    }
  };

  /**
   * To restart the recording
   * switches the modal visibily to false and then to true
   * resulting in the recorder to restart, by unmounting and mounting
   * react-mic component
   */
  restartRecording = () => {
    return new Promise(resolve => {
      this.setState({ modalVisibility: false }, () => {
        resolve();
      });
    }).then(() => {
      this.setState({ modalVisibility: true }, () => {});
    });
  };

  renderButton = () => {
    const {
      buttonComponent,
      header,
      t,
      version,
      buttonContainerStyle,
      onDeleteClick,
      disabled = false,
      showButton = true,
      buttonComponentTooltip,
      testId,
      activeAudioRecorder,
      buttonWrapperStyle,
      loaderStyles,
      "aria-label": ariaLabel,
    } = this.props;
    const { modalVisibility, isInitializing } = this.state;

    const customButtonClass = classNames(
      { [classes.customButton]: true },
      { [classes.customButtonDisabled]: modalVisibility }
    );

    const buttonWrapperClasses = classNames(
      { [classes.buttonWrapper]: true },
      { [classes.disabled]: disabled }
    );

    const buttonTextWrapperClasses = classNames(
      { [classes.buttonTextWrapper]: true },
      { [classes.disabled]: disabled }
    );

    const customButtonText = modalVisibility
      ? t("common:recording_instructions")
      : t("common:add_audio_instructions");

    const discardRecordingAndStartNew = () => {
      activeAudioRecorder?.closeActiveAudioRecorder();
      setAudioRecorderActive(null);
      this.setState({ showDiscardExistingRecordingModal: false });
    };

    const isValidButtonComponent = isValidElement(buttonComponent);

    const buttonSize = (() => {
      const isSizeAvailable =
        isValidButtonComponent &&
        buttonComponent.props &&
        buttonComponent.props.size;

      if (!isSizeAvailable) {
        return "medium";
      }
      return buttonComponent.props.size;
    })();

    const shouldShowLoader =
      version === "inline" && isInitializing && isValidButtonComponent;

    const renderTriggerComponent = () => {
      if (shouldShowLoader) {
        return <LoaderSVG size={buttonSize} loaderStyles={loaderStyles} />;
      }
      if (!buttonComponent) {
        return (
          <MicrophoneOutlined
            size={"xxx-small"}
            variant={"link"}
            disabled={disabled}
          />
        );
      }

      // Merge refs: preserve existing ref and add our own
      return React.cloneElement(buttonComponent, {
        isRecording: modalVisibility,
        ref: mergeRefs(this.buttonRef, buttonComponent.ref),
      });
    };

    const getAriaLabel = () => {
      if (ariaLabel) {
        return ariaLabel;
      }
      if (!buttonComponent) {
        return "Record audio";
      }
      return "";
    };

    const handleRecordButtonKeyDown = e => {
      onEnterOrSpaceKeyDown(e, () => {
        if (!disabled) {
          this.handleAudioRecording();
          this.audioRecorderRef?.current?.focus();
        }
      });
    };

    const handleValidRecordButtonClick = () => {
      if (activeAudioRecorder?.recorderAlreadyActive)
        this.setState({ showDiscardExistingRecordingModal: true });
      else {
        !disabled ? this.handleAudioRecording() : null;
      }
    };

    const handleInternalRecordButtonClick = () => {
      !modalVisibility && !disabled ? this.handleAudioRecording() : () => {};
    };

    const getCommonProps = () => {
      const commonProps = {
        "data-test-id": `${testId}-audioRecorder-button`,
        "aria-label": getAriaLabel(),
        onKeyDown: handleRecordButtonKeyDown,
      };

      if (isValidButtonComponent) {
        return {
          ...commonProps,
          className: buttonWrapperClasses,
          style: buttonWrapperStyle,
          role: "presentation",
          onClick: handleValidRecordButtonClick,
        };
      }

      return {
        ...commonProps,
        className: customButtonClass,
        style: {},
        role: "button",
        tabIndex: 0,
        ref: this.buttonRef,
        onClick: handleInternalRecordButtonClick,
      };
    };

    const renderButton = () => {
      if (!showButton) return null;

      const commonProps = getCommonProps();

      if (isValidButtonComponent) {
        return (
          <ConditionalWrapper
            type={"wrapperAsElement"}
            condition={buttonComponentTooltip}
            wrapper={
              <Tooltip tooltip={buttonComponentTooltip} placement={"top"} />
            }
          >
            <div {...commonProps}>{renderTriggerComponent()}</div>
          </ConditionalWrapper>
        );
      }

      return (
        <div {...commonProps}>
          <span className={buttonTextWrapperClasses}>
            {renderTriggerComponent()}
            <span className={classes.buttonText}>
              {header ? header : customButtonText}
            </span>
          </span>
        </div>
      );
    };

    return (
      <div className={classes.container} style={buttonContainerStyle}>
        {renderButton()}
        {this.state.showDiscardExistingRecordingModal ? (
          <Dialog
            buttonOneText={t("common:continue_recording")}
            buttonTwoText={t("common:discard_and_start_new")}
            message={t("common:discard_current_recording_message")}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
            title={t("common:start_new_recording")}
            onButtonOneClick={() =>
              this.setState({ showDiscardExistingRecordingModal: false })
            }
            onButtonTwoClick={discardRecordingAndStartNew}
            onClose={() =>
              this.setState({ showDiscardExistingRecordingModal: false })
            }
            dsVersion="2.0"
          />
        ) : (
          this.renderRecorder()
        )}
      </div>
    );
  };
  handleAudioRecorderRef = ref => (this.audioRecorderRef = ref);

  renderRecorder = () => {
    const { version, disabled = false } = this.props;
    const { modalVisibility } = this.state;
    if (disabled) {
      return null;
    }

    switch (version) {
      case "v1":
        return modalVisibility ? this.renderAudioRecorderV1() : null;
      case "v2":
        return modalVisibility ? this.renderAudioRecorderV2() : null;
      case "v3":
        return modalVisibility ? this.renderAudioRecorderV3() : null;
      case "visualiser":
        return modalVisibility ? this.renderAudioRecorderVisualiser() : null;
      case "inline":
        return modalVisibility ? this.renderInlineAudioRecorder() : null;
      case "v4":
        return modalVisibility ? this.renderAudioRecorderV4() : null;
      case "v5":
        return modalVisibility ? this.renderAudioRecorderV5() : null;
      default:
        return null;
    }
  };

  renderAudioRecorderV1 = () => {
    const { header, t, testId } = this.props;
    return (
      <UIModal modalContent={classes.modalContent} isOpen={true}>
        <div className={classes.modalContainer}>
          <div className={classes.headerContainer}>
            <div className={classes.header}>
              {header ? header : t("common:add_audio_instructions")}
            </div>
            <div
              data-test-id={`${testId}-audioRecorder-close-button`}
              className={classes.cancelIcon}
              onClick={this.onModalClose}
              role="presentation"
            >
              <CancelIcon width={12} height={12} />
            </div>
          </div>
          <AudioRecorder
            handleAudioFile={this.handleAudioFile}
            styles={styles}
            recordButtonColor="pink"
            ref={this.handleAudioRecorderRef}
            onDraftModalOutsideClick={this.onModalClose}
            testId={`${testId}-audioRecorder`}
          />
        </div>
      </UIModal>
    );
  };

  renderAudioRecorderV2 = () => {
    const {
      container,
      innerContainerStyle,
      audioStripStyle,
      draftRecordingModalTitle,
      stripContainerStyles,
      alertContainerStyle,
      testId,
    } = this.props;

    const { isContainerProvided } = this.state;
    if (container && container.current) {
      this.setState({ isContainerProvided: true });
    }

    if (isContainerProvided) {
      container.current.style.position = "relative";
    }

    return (
      <Portal container={isContainerProvided ? container.current : null}>
        <AudioRecorderV2
          handleAudioFile={this.handleAudioFile}
          handleAudioStripVisibility={this.setModalVisibility(false)}
          innerContainerStyle={innerContainerStyle}
          audioStripStyle={isContainerProvided ? {} : audioStripStyle}
          alertContainerStyle={alertContainerStyle}
          styles={styles}
          recordButtonColor="pink"
          ref={this.handleAudioRecorderRef}
          draftRecordingModalTitle={draftRecordingModalTitle}
          stripContainerStyles={stripContainerStyles}
          onCancel={this.handleCancelRecording}
          testId={`${testId}-audioRecorder`}
        />
      </Portal>
    );
  };

  renderAudioRecorderV3 = () => {
    const { saveDraftRecording, limit, headerLabel } = this.props;
    return (
      <Portal>
        <AudioRecorderV3
          handleAudioFile={this.handleAudioFile}
          ref={this.handleAudioRecorderRef}
          onDraftModalOutsideClick={this.onModalClose}
          limit={limit}
          saveDraftRecording={saveDraftRecording}
          headerLabel={headerLabel}
        />
      </Portal>
    );
  };

  renderAudioRecorderVisualiser = () => {
    const {
      container,
      isSpeechToTextVisualiser = false,
      alertContainerStyle,
      audioRecorderVisualizerStyle,
      speechToTextParams = {},
      source,
      saveDraftRecording,
      minRecordingDurationMs,
    } = this.props;

    const { isContainerProvided, isLoading } = this.state;
    if (container && container.current) {
      this.setState({ isContainerProvided: true });
    }

    if (isContainerProvided) {
      container.current.style.position = "relative";
    }

    /**
     * A ref must be passed to Audio Recorder visualiser for positioning the visualiser
     */

    return (
      <Portal container={isContainerProvided ? container.current : null}>
        <AudioRecorderVisualiser
          handleAudioFile={this.handleAudioFile}
          handleAudioStripVisibility={this.setModalVisibility(false)}
          onCancel={this.handleCancelRecording}
          onDraftModalOutsideClick={this.onModalClose}
          ref={this.handleAudioRecorderRef}
          showVisualiser={true}
          visualiserColor="#6464DC"
          frameRate={30}
          isSpeechToTextVisualiser={isSpeechToTextVisualiser}
          alertContainerStyle={alertContainerStyle}
          audioRecorderVisualizerStyle={audioRecorderVisualizerStyle}
          speechToTextParams={speechToTextParams}
          source={source}
          isLoading={isLoading}
          updateIsLoading={this.updateIsLoading}
          saveDraftRecording={saveDraftRecording}
          minRecordingDurationMs={minRecordingDurationMs}
        />
      </Portal>
    );
  };

  // this is a customised version of visualiser
  // instead of rendering in-place, this renders in a portal
  // and is positioned on top of the record button
  renderInlineAudioRecorder = () => {
    const {
      alertContainerStyle,
      recordButtonRef,
      scrollContainerRef,
      completeButtonSize,
      showTimer,
      containerElement,
      getButtonPositionRect,
      getVisualiserContainerRect,
      minRecordingDurationMs,
    } = this.props;

    const { isInitializing } = this.state;

    return (
      <Portal container={containerElement}>
        <SpeechToTextVisualiser
          handleAudioFile={this.handleAudioFile}
          handleAudioStripVisibility={this.setModalVisibility(false)}
          onCancel={this.handleCancelRecording}
          onDraftModalOutsideClick={this.onModalClose}
          ref={this.handleAudioRecorderRef}
          showVisualiser={true}
          frameRate={30}
          alertContainerStyle={alertContainerStyle}
          recordButtonRef={recordButtonRef}
          scrollContainerRef={scrollContainerRef}
          completeButtonSize={completeButtonSize}
          showTimer={showTimer}
          playBeepOnStart={true}
          isInitializing={isInitializing}
          setIsInitializing={this.setIsInitializing}
          setChatbarVisible={this.props.setChatbarVisible}
          getButtonPositionRect={getButtonPositionRect}
          getVisualiserContainerRect={getVisualiserContainerRect}
          minRecordingDurationMs={minRecordingDurationMs}
        />
      </Portal>
    );
  };

  renderAudioRecorderV4 = () => {
    const {
      limit,
      showLimit,
      title,
      reloadText,
      submitText,
      showRestartDialogV2,
      showStartOverDialogV2,
      permissionWarningAlertStyle,
      testId,
    } = this.props;
    return (
      <AudioRecorderV4
        frameRate={30}
        limit={limit}
        ref={this.handleAudioRecorderRef}
        showLimit={showLimit}
        showVisualiser={true}
        title={title}
        visualiserColor="#222222"
        closeModal={this.onModalClose}
        handleAudioFile={this.handleAudioFile}
        onCancel={this.handleCancelRecording}
        restartRecording={this.restartRecording}
        reloadText={reloadText}
        submitText={submitText}
        showRestartDialogV2={showRestartDialogV2}
        showStartOverDialogV2={showStartOverDialogV2}
        permissionWarningAlertStyle={permissionWarningAlertStyle}
        testId={`${testId}-audio-recorder`}
      />
    );
  };

  renderAudioRecorderV5 = () => {
    const {
      limit,
      showLimit,
      title,
      reloadText,
      submitText,
      showRestartDialogV2,
      showStartOverDialogV2,
      permissionWarningAlertStyle,
      position,
      testId,
      showAudioDownloadButton,
      shouldBeResponsive,
    } = this.props;
    return (
      <AudioRecorderV5
        frameRate={30}
        limit={limit}
        ref={this.handleAudioRecorderRef}
        showLimit={showLimit}
        showVisualiser={true}
        title={title}
        visualiserColor="#cccccc"
        closeModal={this.onModalClose}
        handleAudioFile={this.handleAudioFile}
        onCancel={this.handleCancelRecording}
        restartRecording={this.restartRecording}
        reloadText={reloadText}
        submitText={submitText}
        showRestartDialogV2={showRestartDialogV2}
        showStartOverDialogV2={showStartOverDialogV2}
        permissionWarningAlertStyle={permissionWarningAlertStyle}
        forPlayerVersion5={true}
        position={position}
        testId={`${testId}-audio-recorder`}
        showAudioDownloadButton={showAudioDownloadButton}
        shouldBeResponsive={shouldBeResponsive}
      />
    );
  };

  renderView = () => {
    return this.renderValue();
  };

  renderValue = () => {
    const {
      mode,
      t,
      onDeleteClick,
      downloadFromUrl,
      isLastAttachment,
      dialogsInfo,
      showAudioDownloadButton = true,
      customMediaWrapperStyle,
      showWaveform,
      testId,
      isDownloadEnabled,
      showCompactAudioWaveformPlayer,
      isAppearanceLikeMediaCard,
      customDeleteButton,
      mediaPlaceholderContainerStyle,
      playAudioOnSeek,
    } = this.props;
    const value = this.getValue();

    const isUploading = this.isUploading();
    return this.isValidValue(value) ? (
      <CustomMediaPlaceholder
        onDeleteClick={onDeleteClick}
        attachments={[value]}
        deleteAudio={this.deleteAudio}
        mode={mode}
        isUploading={isUploading}
        t={t}
        downloadFromUrl={downloadFromUrl}
        isLastAttachment={isLastAttachment}
        dialogsInfo={dialogsInfo}
        showAudioDownloadButton={showAudioDownloadButton}
        customMediaWrapperStyle={customMediaWrapperStyle}
        showWaveform={showWaveform}
        testId={testId}
        isDownloadEnabled={isDownloadEnabled}
        showCompactAudioWaveformPlayer={showCompactAudioWaveformPlayer}
        isAppearanceLikeMediaCard={isAppearanceLikeMediaCard}
        customDeleteButton={customDeleteButton}
        mediaPlaceholderContainerStyle={mediaPlaceholderContainerStyle}
        playAudioOnSeek={playAudioOnSeek}
      />
    ) : null;
  };

  isValidValue = value => {
    return !!value && !_.isEmpty(value);
  };

  isUploading = () => {
    const { uploadingObjRedux } = this.props;
    return this.isValidValue(_.get(uploadingObjRedux, "attachment", {}));
  };

  getValue = () => {
    const { uploadingObjRedux, value } = this.props;
    return this.isValidValue(value)
      ? value
      : _.get(uploadingObjRedux, "attachment", {});
  };

  renderEdit = () => {
    const { showValue, uploadingObjRedux } = this.props;
    const value = this.getValue();
    const status = _.get(uploadingObjRedux, "status");
    if (showValue && this.isValidValue(value) && status !== "DOWNLOAD") {
      return this.renderValue();
    } else {
      return this.renderButton();
    }
  };
}

AudioRecorderSelector.defaultProps = {
  ...UIBaseComponent.defaultProps,
  showValue: true,
  parentId: null,
  parentType: "",
  version: "v2",
  isLastAttachment: false,
  dialogsInfo: DIALOGS_INFO,
  setChatbarVisible: () => {},
  showButton: true,
  customMediaWrapperStyle: {},
  buttonComponentTooltip: "",
  testId: "UIComponents",
  isDownloadEnabled: true,
  showProgress: true,
  showCompactAudioWaveformPlayer: false,
  isAppearanceLikeMediaCard: false,
  isSpeechToTextVisualiser: false,
  alertContainerStyle: {},
  showAudioDownloadButton: true,
  playAudioOnSeek: false,
  shouldBeResponsive: false,
  loaderStyles: {},
  shouldAwaitUploadFile: false,
  source: "DEFAULT",
  speechToTextParams: {},
};

AudioRecorderSelector.propTypes = {
  ...UIBaseComponent.propTypes,
  version: PropTypes.oneOf([
    "v1",
    "v2",
    "v3",
    "v4",
    "v5",
    "visualiser",
    "inline",
  ]),
  dialogsInfo: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    confirmButtonText: PropTypes.string,
    cancelButtonText: PropTypes.string,
    label: PropTypes.string,
  }),
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  showButton: PropTypes.bool,
  showTimer: PropTypes.bool,
  customMediaWrapperStyle: PropTypes.object,
  buttonComponentTooltip: PropTypes.string,
  testId: PropTypes.string,
  isDownloadEnabled: PropTypes.bool,
  showProgress: PropTypes.bool,
  onFileUpload: PropTypes.func,
  showCompactAudioWaveformPlayer: PropTypes.bool,
  isAppearanceLikeMediaCard: PropTypes.bool,
  isSpeechToTextVisualiser: PropTypes.bool,
  alertContainerStyle: PropTypes.object,
  showAudioDownloadButton: PropTypes.bool,
  playAudioOnSeek: PropTypes.bool,
  shouldBeResponsive: PropTypes.bool,
  scrollContainerRef: PropTypes.object,
  completeButtonSize: PropTypes.string,
  loaderStyles: PropTypes.object,
  handleExternalUpdates: PropTypes.object,
  shouldAwaitUploadFile: PropTypes.bool,
  source: PropTypes.string,
  speechToTextParams: PropTypes.object,
};

const mapStateToProps = (state, ownProps) => {
  const progressOfUploads = state.app_services.progressOfUploads;

  return {
    showValue: ownProps.showValue,
    progressOfUploads,
    uploadingObjRedux:
      _.find(
        Object.values(progressOfUploads),
        item =>
          item.parentType == ownProps.parentType &&
          item.parentId == ownProps.parentId
      ) || {},
    activeAudioRecorder: _.get(
      state,
      "app_services.activeAudioRecorder",
      false
    ),
  };
};

const mapActionCreators = {
  uploadFile,
  removeProgressElement,
  createAttachment,
  deleteAttachment,
  downloadFromUrl,
  setAudioRecorderActive,
};

export default compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators)
)(AudioRecorderSelector);
